import { GridColDef } from "@mui/x-data-grid";
import { useMenusDispatch, useMenusState } from "./menus-context";
import { ACTION_TYPES, initialState } from "./menus-reducer";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";
import { IMenuListResponse } from "../../../../model/admin-portal/response/menu-list-response";
import { IconButton, Box, Chip, Tooltip } from "@mui/material";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';

export const useMenusController = () => {
    const state = useMenusState();
    const dispatch = useMenusDispatch();
    const { showMessage, showBackDrop, showDialog } = useBaseLayoutController();

    const loadData = async () => {
        try {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: true });
            const response = await AdminPortalAppService.listMenus();
            dispatch({ type: ACTION_TYPES.setDataListResponse, payload: response });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar menus' });
        } finally {
            dispatch({ type: ACTION_TYPES.setIsLoadingData, payload: false });
        }
    };

    const getData = async (id: number) => {
        try {
            showBackDrop(true);
            const response = await AdminPortalAppService.getMenu(id);
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: response });

            // Mapear para o formato do request
            const requestData = {
                id: response.id,
                title: response.title,
                customPageId: response.customPageId || null,
                externalUrl: response.externalUrl || '',
                order: response.order,
                visible: response.visible,
                items: response.items.map((item: any) => ({
                    id: item.id,
                    title: item.title,
                    customPageId: item.customPageId || null,
                    externalUrl: item.externalUrl || '',
                    order: item.order,
                    visible: item.visible,
                    isDefault: item.isDefault
                }))
            };

            dispatch({ type: ACTION_TYPES.setDataRequest, payload: requestData });
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao carregar menu' });
        } finally {
            showBackDrop(false);
        }
    };

    const deleteData = async (id: number) => {
        showDialog({
            title: 'Confirmar exclusão',
            message: 'Deseja realmente excluir este menu?',
            onYesClick: async () => {
                try {
                    showBackDrop(true);
                    await AdminPortalAppService.deleteMenu(id);
                    showMessage({ type: 'success', message: 'Menu excluído com sucesso' });
                    await loadData();
                } catch (error: any) {
                    showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao excluir menu' });
                } finally {
                    showBackDrop(false);
                }
            },
            onNoClick: () => { },
            open: true,
            onClose: () => { }
        });
    };

    const save = async () => {
        if (!validFields()) {
            showMessage({ type: 'warning', message: 'Preencha todos os campos obrigatórios' });
            return;
        }

        try {
            showBackDrop(true);
            await AdminPortalAppService.addUpdateMenu(state.dataRequest);
            showMessage({
                type: 'success',
                message: state.dataRequest.id !== undefined && state.dataRequest.id !== null ? 'Menu atualizado com sucesso' : 'Menu criado com sucesso'
            });
            closeForm();
            await loadData();
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao salvar menu' });
        } finally {
            showBackDrop(false);
        }
    };

    const validFields = (): boolean => {
        if (!state.dataRequest.title || state.dataRequest.title.trim() === '') {
            return false;
        }

        if (state.dataRequest.items.length === 0) {
            return false;
        }

        // Validar cada item
        for (const item of state.dataRequest.items) {
            if (!item.title || item.title.trim() === '') {
                return false;
            }
            // Item deve ter customPageId OU externalUrl
            if (!item.customPageId && (!item.externalUrl || item.externalUrl.trim() === '')) {
                return false;
            }
        }

        return true;
    };

    const handleOnChangeForm = (field: string, value: any) => {
        const updatedRequest = { ...state.dataRequest, [field]: value };

        // Se selecionar customPageId, limpar externalUrl
        if (field === 'customPageId' && value) {
            updatedRequest.externalUrl = '';
        }

        dispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: updatedRequest
        });
    };

    const openForm = (menu?: IMenuListResponse) => {
        if (menu) {
            getData(menu.id);
        } else {
            dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialState.dataRequest });
            dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
        }
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: true });
    };

    const closeForm = () => {
        dispatch({ type: ACTION_TYPES.setShowAddEdit, payload: false });
        dispatch({ type: ACTION_TYPES.setDataRequest, payload: initialState.dataRequest });
        dispatch({ type: ACTION_TYPES.setDataResponse, payload: null });
    };

    // Gestão de items
    const addNewItem = () => {
        const newItem = {
            id: null,
            title: '',
            customPageId: null,
            externalUrl: '',
            order: state.dataRequest.items.length + 1,
            visible: true
        };

        dispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: {
                ...state.dataRequest,
                items: [...state.dataRequest.items, newItem]
            }
        });
    };

    const removeItemByIndex = (index: number) => {
        const newItems = state.dataRequest.items.filter((_, idx) => idx !== index);
        dispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: { ...state.dataRequest, items: newItems }
        });
    };

    const updateItem = (itemIndex: number, field: string, value: any) => {
        const newItems = [...state.dataRequest.items];
        newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };

        // Se selecionar customPageId, limpar externalUrl
        if (field === 'customPageId' && value) {
            newItems[itemIndex].externalUrl = '';
        }

        dispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: { ...state.dataRequest, items: newItems }
        });
    };

    const reorderItems = (oldIndex: number, newIndex: number) => {
        const newItems = [...state.dataRequest.items];
        const [removed] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, removed);

        // Atualizar orders
        newItems.forEach((item, idx) => {
            item.order = idx + 1;
        });

        dispatch({
            type: ACTION_TYPES.setDataRequest,
            payload: { ...state.dataRequest, items: newItems }
        });
    };

    // Reordenação de menus
    const handleReorderMenus = async (menuIds: number[]) => {
        try {
            await AdminPortalAppService.reorderMenus(menuIds);
            await loadData();
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao reordenar menus' });
        }
    };

    const handleReorderMenuItems = async (itemIds: number[]) => {
        try {
            await AdminPortalAppService.reorderMenuItems(itemIds);
            await loadData();
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao reordenar itens' });
        }
    };

    // Custom pages
    const loadCustomPages = async () => {
        try {
            const response = await AdminPortalAppService.listAllCustomPages(true);
            dispatch({ type: ACTION_TYPES.setCustomPagesList, payload: response });
        } catch (error: any) {
            console.error('Erro ao carregar páginas customizadas:', error);
        }
    };

    const getColumnsGrid = (): GridColDef[] => {
        return [
            {
                field: 'title',
                headerName: 'Título',
                flex: 1,
                minWidth: 200,
                renderCell: (params) => (
                    <Box display="flex" alignItems="center" gap={1}>
                        {params.row.isDefault && (
                            <Chip label="Padrão" size="small" color="primary" />
                        )}
                        <span>{params.value}</span>
                    </Box>
                )
            },
            {
                field: 'slug',
                headerName: 'Slug',
                flex: 1,
                minWidth: 150
            },
            {
                field: 'order',
                headerName: 'Ordem',
                width: 100,
                align: 'center',
                headerAlign: 'center'
            },
            {
                field: 'visible',
                headerName: 'Visível',
                width: 100,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => (
                    params.value ? <VisibilityIcon color="success" /> : <VisibilityOffIcon color="disabled" />
                )
            },
            {
                field: 'items',
                headerName: 'Itens',
                width: 100,
                align: 'center',
                headerAlign: 'center',
                renderCell: (params) => params.value?.length || 0
            },
            {
                field: 'actions',
                headerName: 'Ações',
                width: 120,
                align: 'center',
                headerAlign: 'center',
                sortable: false,
                renderCell: (params) => {
                    const isHome = params.row.slug?.toLowerCase() === 'home';
                    return (
                    <Box display="flex" gap={1}>
                        <Tooltip title={isHome ? 'Menu HOME não pode ser editado' : 'Editar'}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openForm(params.row);
                                    }}
                                    disabled={isHome}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                        <Tooltip title={isHome ? 'Menu HOME não pode ser excluído' : 'Excluir'}>
                            <span>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        deleteData(params.row.id);
                                    }}
                                    disabled={isHome}
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>
                    );
                }
            }
        ];
    };

    return {
        ...state,
        loadData,
        getData,
        deleteData,
        save,
        validFields,
        handleOnChangeForm,
        openForm,
        closeForm,
        addNewItem,
        removeItemByIndex,
        updateItem,
        reorderItems,
        handleReorderMenus,
        handleReorderMenuItems,
        loadCustomPages,
        getColumnsGrid
    };
};
